import React, { useContext, useEffect, useState } from 'react';
import { sortBy } from 'lodash';
import { Button, Table, Modal, StatusIndicator } from '@amzn/awsui-components-react-v3';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import shortUUID from 'short-uuid';

import { preloadLab, endLabs } from 'modules/api';
import messages from './IngressTable.messages';
import { messages as studentRosterTableMessages } from '../studentRosterTable/StudentRosterTable.messages';
import './IngressTable.scss';
import {
    allAlertBannerItems,
    computeTableItems,
    computeTableForPooledLabs,
    getLabStatus,
    hiddenAlertBannerItem,
    isPreloadFailed,
    isExceedingLabLimit,
    isPooledLab,
    LabStatus,
    buildExceedLabLimitBannerItem,
    isAllConsoleReady,
    isAllPreloadingEnded,
} from './IngressTable.utils';
import IngressTableHeader from '../ingressTableHeader/IngressTableHeader';
import { ClassroomContext } from 'data/ClassroomContext';
import { nowInUnixTimeInSec } from 'utils/timestamp-utils';
import EndLabsModal from 'components/endLabsModal/EndLabsModal';
import { removeCourseNameFromLabTitle, useFlags } from 'utils';

const {
    tableHeaderLabStatus,
    tableHeaderStudent,
    tablePreloadingLabStatus,
    tablePreloadedLabStatus,
    tableEndedLabStatus,
    tableFailedLabStatus,
    studentLabEnded,
    cannotIngress,
    close,
    tableEmpty,
    closeModalLabelText,
    tableExceedLabLimit,
    tableRunningLabStatus,
    tableExpiredLabStatus,
} = messages;

const IngressTable = ({
    studentTrainings = [],
    loading,
    trainingLoading,
    trainingRefetch,
    labId,
    langLocale,
    hasStudentRoster,
    setAlertBannerItem,
    setRegionBannerItem,
    setWarningBannerItem,
    globals = window,
}) => {
    const { formatMessage } = useIntl();
    const flags = useFlags();
    const { classroomId, labNumber } = useParams();
    const {
        classData: { classroom, content, course },
    } = useContext(ClassroomContext);
    const [isSending, setIsSending] = useState(false);
    const [isError, setError] = useState(false);
    const [batchId, batchIdSet] = useState();
    const [selectedItems, selectedItemsSet] = useState([]);
    const [showEndLabsModal, showEndLabsModalSet] = useState(false);

    const {
        successAlertBannerItem,
        errorAlertBannerItem,
        inProgressAlertBannerItem,
        hasNotStartedAlertBannerItem,
    } = allAlertBannerItems(formatMessage, setAlertBannerItem);

    const exceedLabLimitBannerItem = buildExceedLabLimitBannerItem(
        formatMessage,
        setWarningBannerItem
    );

    useEffect(() => {
        if (batchId && !batchId.includes(labId)) {
            batchIdSet(null);
        }
    }, [labId, batchId]);

    useEffect(() => {
        if (classroom.startsOn > nowInUnixTimeInSec()) {
            setAlertBannerItem(hasNotStartedAlertBannerItem);
        } else if (batchId && isAllConsoleReady(studentTrainings, labId, batchId)) {
            setAlertBannerItem(successAlertBannerItem);
        } else if (
            batchId &&
            studentTrainings.some(
                training =>
                    training.arn === labId &&
                    isPreloadFailed(training) &&
                    training?.requestClientToken?.includes(batchId)
            )
        ) {
            setAlertBannerItem(errorAlertBannerItem);
        } else if (batchId && isAllPreloadingEnded(studentTrainings, labId, batchId)) {
            setAlertBannerItem(hiddenAlertBannerItem);
        } else if (batchId) {
            setAlertBannerItem(inProgressAlertBannerItem);
        } else {
            setAlertBannerItem(hiddenAlertBannerItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labId, JSON.stringify(studentTrainings), batchId]);

    useEffect(() => {
        selectedItemsSet([]);
    }, [labId, selectedItemsSet]);

    useEffect(() => {
        if (
            batchId &&
            studentTrainings.some(
                training =>
                    training.arn === labId &&
                    isExceedingLabLimit(training?.fulfillmentError?.name) &&
                    training?.requestClientToken?.includes(batchId)
            )
        ) {
            setWarningBannerItem(exceedLabLimitBannerItem);
        } else {
            setWarningBannerItem(hiddenAlertBannerItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labId, JSON.stringify(studentTrainings), batchId]);

    const performBatchProvisioning = async preferredRegions => {
        if (isSending) return;
        setIsSending(true);
        const pooledCountFilterRegex = /(unassigned|unregistered|pooled)-/i;
        const userKeys = flags?.endLabs ? (selectedItems || []).map(s => s.userKey) : [];
        const pooledLabCount = userKeys.filter(u => pooledCountFilterRegex.test(u)).length;
        const filteredUserKeys = userKeys.filter(u => !pooledCountFilterRegex.test(u));
        try {
            const result = await preloadLab({
                classroomId,
                blueprintArn: labId,
                clientToken: shortUUID.generate(),
                preferredLangLocale: langLocale,
                targetRegions: preferredRegions,
                ...(filteredUserKeys?.length ? { userKeys: filteredUserKeys } : {}),
                ...(pooledLabCount && pooledLabCount > 0 ? { pooledLabCount } : {}),
            });
            batchIdSet(
                result?.createStudentTrainings?.batchId ||
                    result.createStudentTrainings?.labPoolBatchInfo?.batchId
            );
        } catch (error) {
            console.error(error);
            setAlertBannerItem(errorAlertBannerItem);
        } finally {
            setIsSending(false);
        }
    };

    const performEndLabsCall = async () => {
        await endLabs({
            blueprintArn: labId,
            classroomId,
            clientToken: shortUUID.generate(),
            userKeys: (selectedItems || []).map(s => s.userKey),
        });
        showEndLabsModalSet(false);
        setTimeout(trainingRefetch, 2000);
    };

    const formatLabStatus = status => {
        switch (status) {
            case LabStatus.Preloading:
                return (
                    <StatusIndicator type="info">
                        {formatMessage(tablePreloadingLabStatus)}
                    </StatusIndicator>
                );
            case LabStatus.Ready:
            case LabStatus.Preloaded:
                return (
                    <StatusIndicator type="success">
                        {formatMessage(tablePreloadedLabStatus)}
                    </StatusIndicator>
                );
            case LabStatus.Ended:
                return (
                    <StatusIndicator type="stopped">
                        {formatMessage(tableEndedLabStatus)}
                    </StatusIndicator>
                );
            case LabStatus.Failed:
                return (
                    <StatusIndicator type="error">
                        {formatMessage(tableFailedLabStatus)}
                    </StatusIndicator>
                );
            case LabStatus.ExceedLabLimit:
                return (
                    <StatusIndicator type="warning">
                        {formatMessage(tableExceedLabLimit)}
                    </StatusIndicator>
                );
            case LabStatus.Running:
                return (
                    <StatusIndicator type="in-progress">
                        {formatMessage(tableRunningLabStatus)}
                    </StatusIndicator>
                );
            case LabStatus.Expired:
                return (
                    <StatusIndicator type="stopped">
                        {formatMessage(tableExpiredLabStatus)}
                    </StatusIndicator>
                );
            case LabStatus.NoLab:
            // fall through
            default:
                return '-';
        }
    };

    const columnDefinitions = [
        !hasStudentRoster && {
            id: 'student',
            header: formatMessage(tableHeaderStudent),
            cell: ({ studentNumber }) => studentNumber || '-',
        },
        hasStudentRoster && {
            id: 'firstName',
            header: formatMessage(studentRosterTableMessages.headerFirstName),
            cell: ({ firstName }) => firstName || '-',
        },
        hasStudentRoster && {
            id: 'lastName',
            header: formatMessage(studentRosterTableMessages.headerLastName),
            cell: ({ lastName }) => lastName || '-',
        },
        hasStudentRoster &&
            flags?.pooledLabs && {
                id: 'email',
                header: formatMessage(studentRosterTableMessages.headerEmail),
                cell: ({ email }) => email || '-',
            },
        {
            id: 'labRegion',
            header: formatMessage(studentRosterTableMessages.headerLabRegion),
            cell: ({ labRegion, metaData }) =>
                labRegion || (metaData?.labRegions?.length && metaData?.labRegions[0]) || '-',
        },
        {
            id: 'labStatus',
            header: formatMessage(tableHeaderLabStatus),
            cell: lab => formatLabStatus(getLabStatus(lab)),
        },
    ].filter(columnDefinition => columnDefinition);

    const unsortedItems = flags?.pooledLabs
        ? computeTableForPooledLabs(studentTrainings, labId, classroom)
        : computeTableItems(
              (studentTrainings || []).filter(t => !isPooledLab(t)),
              labId
          );
    const tableItems = sortBy(unsortedItems, ['sortingRank', 'studentNumber']);

    const onSelectionChange = ({ detail }) => {
        selectedItemsSet(detail.selectedItems);
    };
    const sanitizedLabTitle = removeCourseNameFromLabTitle(
        content[parseInt(labNumber) - 1]?.title,
        course?.title
    );
    return (
        <>
            <Modal
                visible={isError}
                onDismiss={() => setError(false)}
                header={formatMessage(cannotIngress)}
                closeLabel={formatMessage(closeModalLabelText)}
                footer={
                    <span className="awsui-util-f-r">
                        <Button onClick={() => setError(false)} variant="primary">
                            {formatMessage(close)}
                        </Button>
                    </span>
                }
            >
                {formatMessage(studentLabEnded)}
            </Modal>
            <EndLabsModal
                visible={showEndLabsModal}
                onCancel={() => showEndLabsModalSet(false)}
                labTitle={sanitizedLabTitle}
                endDate={classroom.endsOn}
                onConfirm={performEndLabsCall}
                numberOfStudents={selectedItems?.length}
                classCapacity={classroom?.classCapacity}
            />
            <Table
                selectionType={flags?.endLabs ? 'multi' : 'single'}
                selectedItems={selectedItems}
                onSelectionChange={onSelectionChange}
                loading={loading}
                trackBy={'userKey'}
                stickyHeader
                header={
                    <IngressTableHeader
                        {...{
                            isSending,
                            performBatchProvisioning,
                            performEndLab: () => showEndLabsModalSet(true),
                            trainingLoading,
                            trainingRefetch,
                            errorSet: setError,
                            isSendingSet: setIsSending,
                            selectedItems,
                            globals,
                            tableItems,
                            setRegionBannerItem,
                        }}
                    />
                }
                columnDefinitions={columnDefinitions}
                items={tableItems}
                isItemDisabled={item => item.userKey.startsWith('pooled')}
                empty={
                    <div className="awsui-util-t-c">
                        <div className="awsui-util-pt-s awsui-util-mb-xs">
                            <b>{formatMessage(tableEmpty)}</b>
                        </div>
                    </div>
                }
            ></Table>
        </>
    );
};

export default IngressTable;
